import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useWA, Card, Btn, Field, Skeleton, ErrorBox, Badge, fdt } from "../ui";
import RichEditor from "../RichEditor";
import { siteLink } from "../api";

const slugify = (s) => s.toLowerCase().trim().replace(/[\s_]+/g, "-").replace(/[^\p{L}\p{N}\p{M}-]/gu, "").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

export default function PageEditor() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const { api, run, confirm } = useWA();
  const [f, setF] = useState({ title: "", slug: "", content: "", seoDescription: "" });
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const set = (k, v) => {
    setF((x) => ({ ...x, [k]: v }));
    setDirty(true);
  };

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const r = await api("pageGet", { id });
      setF({ title: r.page.title, slug: r.page.slug, content: r.page.content, seoDescription: r.page.seoDescription || "" });
      setMeta(r.page);
      setDirty(false);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [api, id, isNew]);
  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const r = await run("pageSave", { ...f, id }, "Page save हो गया");
      setDirty(false);
      if (isNew) nav(`/website/pages/${r.id}`, { replace: true });
      else load();
    } catch (e) {
      /* toast */
    }
    setSaving(false);
  }, [f, id, isNew, run, nav, load]);

  useEffect(() => {
    const k = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [save]);

  const del = async () => {
    if (!(await confirm({ title: "यह page delete करें?", danger: true }))) return;
    try {
      await run("pageDelete", { id }, "Page delete हो गया");
      nav("/website/pages");
    } catch (e) {
      /* toast */
    }
  };

  if (loading) return <Skeleton rows={8} h={56} />;
  if (error) return <ErrorBox msg={error} retry={load} />;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Link to="/website/pages" className="w-10 h-10 rounded-xl grid place-items-center border wa-line hover:bg-slate-500/10" aria-label="Back"><i className="fa-solid fa-arrow-left" /></Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-extrabold truncate">{isNew ? "नया Page" : f.title}</h1>
          <p className="text-xs wa-muted">{meta ? `बदला ${fdt(meta.updatedAt)}` : "Title, address और content भरें"} {dirty && <Badge tone="amber" className="ml-1">Save नहीं हुआ</Badge>}</p>
        </div>
        {meta && <a href={siteLink("/" + meta.slug)} target="_blank" rel="noreferrer"><Btn icon="fa-solid fa-arrow-up-right-from-square">Site पर देखें</Btn></a>}
        {!isNew && <Btn className="!text-rose-600" icon="fa-regular fa-trash-can" onClick={del}>Delete</Btn>}
        <Btn variant="primary" icon="fa-solid fa-floppy-disk" loading={saving} onClick={save}>Save</Btn>
      </div>
      <div className="space-y-5">
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Page title"><input className="wa-input" value={f.title} onChange={(e) => set("title", e.target.value)} /></Field>
            <Field label="Address" hint={`ycnp.in/${f.slug || slugify(f.title) || "page-ka-naam"}`}>
              <input className="wa-input" value={f.slug} placeholder={slugify(f.title)} onChange={(e) => set("slug", e.target.value)} />
            </Field>
            <Field label="SEO description" className="md:col-span-2" right={`${f.seoDescription.length}/160`}>
              <input className="wa-input" value={f.seoDescription} maxLength={200} onChange={(e) => set("seoDescription", e.target.value)} />
            </Field>
          </div>
        </Card>
        <RichEditor value={f.content} onChange={(v) => set("content", v)} placeholder="Page का content लिखें…" />
      </div>
    </div>
  );
}
